// sealOps.ts — immutable ops for the board's seals beyond the lock panel: ice (FrozenBox), the super mystery
// box, the sliding door, the laser gate, the bolt and the metal box. Same contract as templateOps: every op
// returns a NEW template when and only when something changed, so the page can read "did this do anything"
// off identity alone.
//
// They come in three shapes, and the game reads them that way (TWGS.Gameplay CellModifier):
//   * a SINGLE COVER over one box — ice and the super mystery box. Both corners are the same cell; the game
//     collapses anything else to cells[0] (NormalizeFrozenBox / NormalizeSuperMysteryBox).
//   * a RUN between two ends on one row or column — the sliding door (cells[0] = ANCHOR, the housing it
//     retracts into; cells[1] = the far end it gives up first), the laser gate (cells[0..1] = the two
//     emitter ends, cells[2] = the KEY box that opens it, standing somewhere else on the board) and the
//     bolt (cells[0] = HEAD — only the box just beyond it unlocks the bolt; cells[1] = the far end).
//   * the METAL BOX, a 2×2 plate (cells[0..1] = its corners) that takes three hits from boxes dropped beside it.
import { BOLT_MIN_LENGTH, CellModifierType, ELEVATOR_SIZE, FROZEN_BOX_HITS, GLASS_PANEL_DEFAULT_COUNT, GLASS_PANEL_GOALS, GLASS_PANEL_SIZE, LASER_GATE_MIN_LENGTH, METAL_BOX_HITS, SLIDING_DOOR_MIN_LENGTH, SUPER_MYSTERY_BOX_STAGES, } from "../Models/CellModifier";
import { BOX_COLORS } from "../Models/LevelData";
import { findCell, isWall, modifierCovers, withCellAdded } from "./templateOps";
// Every type that seals a box — what the inspector lists for a selected cell, and what the overlap rule in
// validateTemplate counts. The connector is the one modifier that is not a seal.
export const SEAL_TYPES = new Set([
    CellModifierType.LockPanel,
    CellModifierType.MetalBox,
    CellModifierType.Bolt,
    CellModifierType.FrozenBox,
    CellModifierType.LaserGate,
    CellModifierType.SlidingDoor,
    CellModifierType.SuperMysteryBox,
    CellModifierType.Elevator,
    CellModifierType.GlassPanel,
]);
export function isSingleCover(type) {
    return type === CellModifierType.FrozenBox || type === CellModifierType.SuperMysteryBox;
}
export function isRun(type) {
    return type === CellModifierType.SlidingDoor || type === CellModifierType.LaserGate || type === CellModifierType.Bolt;
}
// The indices of every modifier that seals (x, y), in list order — a run's key box counts for its gate,
// because that is the cell the author clicks to find out what opens the gate.
export function sealIndicesAt(t, x, y) {
    const out = [];
    t.cells.modifiers.forEach((m, i) => {
        if (m && SEAL_TYPES.has(m.type) && modifierCovers(m, x, y)) {
            out.push(i);
        }
    });
    return out;
}
// --- single covers --------------------------------------------------------------------------------------
// The index of the ice or super mystery box over (x, y), or -1.
export function singleCoverIndexAt(t, x, y) {
    return t.cells.modifiers.findIndex((m) => m && isSingleCover(m.type) && Array.isArray(m.cells) && m.cells.length > 0 && m.cells[0].x === x && m.cells[0].y === y);
}
// Why a single cover cannot go on (x, y), or null when it can. Only a BOX can be covered: a gate, a tunnel or
// a super tunnel is a wall that never leaves, and a cover over one would never lift anything.
export function singleCoverProblem(t, x, y) {
    const cell = findCell(t, x, y);
    return cell && isWall(cell) ? "Ice and the super mystery box cover a box — a gate or a tunnel is a wall." : null;
}
// Puts ice or a super mystery box over the box at (x, y) — adding a plain box first when the cell is empty,
// which is what a click on an empty cell means with the tool held. Whatever box flags the box already has
// (mystery, arrow, colour, group) are left alone: the cover sits OVER the box, it does not replace it.
//
// One cover per box: placing the other kind swaps it in. Placing the same kind again is a no-op (same
// reference back), so a repeated click does not reset ice an author had set to one hit.
export function withSingleCover(t, x, y, type) {
    if (singleCoverProblem(t, x, y) !== null) {
        return t;
    }
    const existing = singleCoverIndexAt(t, x, y);
    if (existing >= 0 && t.cells.modifiers[existing].type === type) {
        return t;
    }
    const mod = {
        type,
        cells: [{ x, y }, { x, y }],
        counter: type === CellModifierType.FrozenBox ? FROZEN_BOX_HITS : SUPER_MYSTERY_BOX_STAGES,
    };
    const base = withCellAdded(t, x, y);
    const modifiers = existing >= 0
        ? base.cells.modifiers.map((m, i) => (i === existing ? mod : m))
        : [...base.cells.modifiers, mod];
    return { ...base, cells: { ...base.cells, modifiers } };
}
// Sets how many hits the ice at `index` still takes: 2 is fresh ice, 1 is ice that starts cracked. The game
// has art for exactly those two states, so anything else is clamped into them.
export function withIceHits(t, index, hits) {
    const m = t.cells.modifiers[index];
    const h = Math.max(1, Math.min(FROZEN_BOX_HITS, Math.floor(hits) || FROZEN_BOX_HITS));
    if (!m || m.type !== CellModifierType.FrozenBox || m.counter === h) {
        return t;
    }
    return withModifierAt(t, index, { ...m, counter: h });
}
// --- runs -----------------------------------------------------------------------------------------------
// How many cells the run from `a` to `b` spans, both ends included.
export function runLength(a, b) {
    return Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) + 1;
}
// The cells from `a` to `b`, walking from `a` — empty unless the two share a row or a column. Bounded by the
// board, because the ends of a hand-edited file can be anything and this runs on every draw.
export function runCells(a, b, limit = 64) {
    if (a.x !== b.x && a.y !== b.y) {
        return [];
    }
    const n = Math.min(limit, runLength(a, b));
    const dx = Math.sign(b.x - a.x);
    const dy = Math.sign(b.y - a.y);
    return Array.from({ length: n }, (_, i) => ({ x: a.x + dx * i, y: a.y + dy * i }));
}
function onBoard(t, p) {
    return p.x >= 0 && p.y >= 0 && p.x < t.columns && p.y < t.rows;
}
function sameRun(m, a, b) {
    return Array.isArray(m.cells) && m.cells.length >= 2 && m.cells[0].x === a.x && m.cells[0].y === a.y && m.cells[1].x === b.x && m.cells[1].y === b.y;
}
// Whether a run of `type` shares any cell with the run from `a` to `b` — a new door drops the doors it lies
// across, the way a new lock drops the locks it overlaps (two tapes over one box would be ambiguous to the
// author and to the game).
function runsOverlap(m, a, b) {
    if (!Array.isArray(m.cells) || m.cells.length < 2) {
        return false;
    }
    const mine = runCells(a, b);
    return runCells(m.cells[0], m.cells[1]).some((p) => mine.some((q) => q.x === p.x && q.y === p.y));
}
// Why a run of `type` cannot go from `a` to `b`, or null when it can. Worded for the toolbar hint.
export function runProblem(t, type, a, b) {
    const name = type === CellModifierType.SlidingDoor ? "A sliding door" : type === CellModifierType.Bolt ? "A bolt" : "A laser gate";
    const min = type === CellModifierType.SlidingDoor ? SLIDING_DOOR_MIN_LENGTH : type === CellModifierType.Bolt ? BOLT_MIN_LENGTH : LASER_GATE_MIN_LENGTH;
    if (!onBoard(t, a) || !onBoard(t, b)) {
        return `${name} has to stay on the board.`;
    }
    if (a.x !== b.x && a.y !== b.y) {
        return `${name} runs along one row or one column — pick an end in line with the first.`;
    }
    if (runLength(a, b) < min) {
        return `${name} covers at least ${min} cells — pick a different end.`;
    }
    if (type === CellModifierType.Bolt) {
        const u = boltUnlockCell(a, b);
        if (!u || !onBoard(t, u)) {
            return "A bolt's head can't sit against the edge — the box that unlocks it would be off the board.";
        }
    }
    return null;
}
// The cell whose box unlocks a bolt with its HEAD at `head`: the one just beyond the head, continuing the
// bolt's own line (the game's CellModifier.BoltUnlockCell). Null for a run that is not straight.
export function boltUnlockCell(head, far) {
    const dx = head.x - far.x;
    const dy = head.y - far.y;
    if ((dx !== 0) === (dy !== 0)) {
        return null;
    }
    return { x: head.x + Math.sign(dx), y: head.y + Math.sign(dy) };
}
// Drives a bolt from its HEAD `head` to `far`. Bolts it lies across are replaced. Same reference back when it
// cannot go there, or the identical bolt is already there.
export function withBolt(t, head, far) {
    if (runProblem(t, CellModifierType.Bolt, head, far) !== null) {
        return t;
    }
    const mods = t.cells.modifiers;
    if (mods.some((m) => m && m.type === CellModifierType.Bolt && sameRun(m, head, far))) {
        return t;
    }
    const bolt = { type: CellModifierType.Bolt, cells: [{ ...head }, { ...far }], counter: 1 };
    const kept = mods.filter((m) => !(m && m.type === CellModifierType.Bolt && runsOverlap(m, head, far)));
    return { ...t, cells: { ...t.cells, modifiers: [...kept, bolt] } };
}
// --- metal box ------------------------------------------------------------------------------------------
// The game ships one metal box shape: a 2×2 (NormalizeMetalBox implies it from the top-left cell).
export const METAL_BOX_SIZE = 2;
// The corners of the metal box a click at (x, y) places: (x, y) is its top-left, moved back onto the board
// when it would hang off the right or bottom edge (the lock panel's rule).
function metalBoxCorners(t, x, y) {
    const ax = Math.max(0, Math.min(x, t.columns - METAL_BOX_SIZE));
    const ay = Math.max(0, Math.min(y, t.rows - METAL_BOX_SIZE));
    return [{ x: ax, y: ay }, { x: Math.min(t.columns - 1, ax + METAL_BOX_SIZE - 1), y: Math.min(t.rows - 1, ay + METAL_BOX_SIZE - 1) }];
}
// The rectangle a metal box covers (a lone cells[0] is its top-left, the 2×2 implied).
function metalBoxRect(m) {
    if (!Array.isArray(m.cells) || m.cells.length === 0) {
        return null;
    }
    const a = m.cells[0];
    const b = m.cells[1] ?? { x: a.x + 1, y: a.y + 1 };
    return [{ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) }, { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) }];
}
// Lays a 2×2 metal box with (x, y) as its top-left, at full strength. Metal boxes it overlaps are replaced (two
// plates over one box would be ambiguous). Same reference back when the identical box is already there — its
// hit count included, so a repeated click does not reset one the author tuned.
export function withMetalBox(t, x, y) {
    const [a, b] = metalBoxCorners(t, x, y);
    const mods = t.cells.modifiers;
    if (mods.some((m) => m && m.type === CellModifierType.MetalBox && sameRun(m, a, b))) {
        return t;
    }
    const overlaps = (m) => {
        if (!m || m.type !== CellModifierType.MetalBox) {
            return false;
        }
        const r = metalBoxRect(m);
        return r !== null && r[0].x <= b.x && r[1].x >= a.x && r[0].y <= b.y && r[1].y >= a.y;
    };
    const plate = { type: CellModifierType.MetalBox, cells: [a, b], counter: METAL_BOX_HITS };
    return { ...t, cells: { ...t.cells, modifiers: [...mods.filter((m) => !overlaps(m)), plate] } };
}
// Sets how many hits the metal box at `index` still takes (1..3 — the game's three chain states).
export function withMetalHits(t, index, hits) {
    const m = t.cells.modifiers[index];
    const h = Math.max(1, Math.min(METAL_BOX_HITS, Math.floor(hits) || METAL_BOX_HITS));
    if (!m || m.type !== CellModifierType.MetalBox || m.counter === h) {
        return t;
    }
    return withModifierAt(t, index, { ...m, counter: h });
}
// --- elevator -------------------------------------------------------------------------------------------
export function withElevator(t, x, y) {
    const ax = Math.max(0, Math.min(x, t.columns - ELEVATOR_SIZE));
    const ay = Math.max(0, Math.min(y, t.rows - ELEVATOR_SIZE));
    const a = { x: ax, y: ay };
    const b = { x: ax + ELEVATOR_SIZE - 1, y: ay + ELEVATOR_SIZE - 1 };
    const mods = t.cells.modifiers;
    if (mods.some((m) => m && m.type === CellModifierType.Elevator && sameRun(m, a, b))) {
        return t;
    }
    const overlaps = (m) => m && m.type === CellModifierType.Elevator && Array.isArray(m.cells) && m.cells.length >= 2 &&
        m.cells[0].x <= b.x && m.cells[1].x >= a.x && m.cells[0].y <= b.y && m.cells[1].y >= a.y;
    const spawns = Array.from({ length: ELEVATOR_SIZE * ELEVATOR_SIZE }, () => ({ group: 0, sibling: 0, sortOrder: 0 }));
    const lift = { type: CellModifierType.Elevator, cells: [a, b], counter: 1, spawns };
    return { ...t, cells: { ...t.cells, modifiers: [...mods.filter((m) => !overlaps(m)), lift] } };
}
export function elevatorSpawnAt(m, index) {
    return m.spawns?.[index] ?? { group: 0, sibling: 0, sortOrder: 0 };
}
export function withElevatorSpawn(t, index, slot, patch) {
    const m = t.cells.modifiers[index];
    if (!m || m.type !== CellModifierType.Elevator) {
        return t;
    }
    const spawns = Array.from({ length: ELEVATOR_SIZE * ELEVATOR_SIZE }, (_, i) => elevatorSpawnAt(m, i));
    spawns[slot] = { ...spawns[slot], ...patch };
    return withModifierAt(t, index, { ...m, spawns });
}
// --- glass panel ----------------------------------------------------------------------------------------
export function glassPanelGoals(m) {
    return Array.isArray(m.goals) ? m.goals : [];
}
export function withGlassPanel(t, x, y) {
    const ax = Math.max(0, Math.min(x, t.columns - GLASS_PANEL_SIZE));
    const ay = Math.max(0, Math.min(y, t.rows - GLASS_PANEL_SIZE));
    const a = { x: ax, y: ay };
    const b = { x: ax + GLASS_PANEL_SIZE - 1, y: ay + GLASS_PANEL_SIZE - 1 };
    const mods = t.cells.modifiers;
    if (mods.some((m) => m && m.type === CellModifierType.GlassPanel && sameRun(m, a, b))) {
        return t;
    }
    const overlaps = (m) => m && m.type === CellModifierType.GlassPanel && Array.isArray(m.cells) && m.cells.length >= 2 &&
        m.cells[0].x <= b.x && m.cells[1].x >= a.x && m.cells[0].y <= b.y && m.cells[1].y >= a.y;
    const cap = {
        type: CellModifierType.GlassPanel,
        cells: [a, b],
        counter: 1,
        goals: BOX_COLORS.slice(0, GLASS_PANEL_GOALS).map((color) => ({ color, count: GLASS_PANEL_DEFAULT_COUNT })),
    };
    return { ...t, cells: { ...t.cells, modifiers: [...mods.filter((m) => !overlaps(m)), cap] } };
}
export function withGlassPanelGoal(t, index, goalIndex, patch) {
    const m = t.cells.modifiers[index];
    if (!m || m.type !== CellModifierType.GlassPanel) {
        return t;
    }
    return withModifierAt(t, index, { ...m, goals: glassPanelGoals(m).map((g, i) => (i === goalIndex ? { ...g, ...patch } : g)) });
}
// Why `key` cannot open the laser gate from `a` to `b`, or null when it can: the key is a box the player
// DROPS, so it has to be a box, and it cannot stand inside the run it opens (it would be sealed by it).
export function laserKeyProblem(t, a, b, key) {
    if (runCells(a, b).some((p) => p.x === key.x && p.y === key.y)) {
        return "The key box has to stand outside the gate's own run.";
    }
    const cell = findCell(t, key.x, key.y);
    if (!cell) {
        return "The key has to go on a box — this cell is empty.";
    }
    if (isWall(cell)) {
        return "The key has to go on a box — a gate or a tunnel is a wall.";
    }
    return null;
}
// Lays a sliding door from its ANCHOR `a` (the housing) to its far end `b`, at full length. The game counts
// its Counter as the door's CURRENT length, so a fresh door's counter is the whole run. Doors it lies across
// are replaced. Same reference back when it cannot go there, or the identical door is already there.
export function withSlidingDoor(t, a, b) {
    if (runProblem(t, CellModifierType.SlidingDoor, a, b) !== null) {
        return t;
    }
    const mods = t.cells.modifiers;
    if (mods.some((m) => m && m.type === CellModifierType.SlidingDoor && sameRun(m, a, b))) {
        return t;
    }
    const door = { type: CellModifierType.SlidingDoor, cells: [{ ...a }, { ...b }], counter: runLength(a, b) };
    const kept = mods.filter((m) => !(m && m.type === CellModifierType.SlidingDoor && runsOverlap(m, a, b)));
    return { ...t, cells: { ...t.cells, modifiers: [...kept, door] } };
}
// Lays a laser gate across `a`..`b`, opened by dropping the box at `key`. Laser gates it lies across are
// replaced. Same reference back when it cannot go there, or the identical gate is already there.
export function withLaserGate(t, a, b, key) {
    if (runProblem(t, CellModifierType.LaserGate, a, b) !== null || laserKeyProblem(t, a, b, key) !== null) {
        return t;
    }
    const mods = t.cells.modifiers;
    const identical = (m) => m && m.type === CellModifierType.LaserGate && sameRun(m, a, b) && m.cells.length >= 3 && m.cells[2].x === key.x && m.cells[2].y === key.y;
    if (mods.some(identical)) {
        return t;
    }
    const gate = { type: CellModifierType.LaserGate, cells: [{ ...a }, { ...b }, { ...key }], counter: 1 };
    const kept = mods.filter((m) => !(m && m.type === CellModifierType.LaserGate && runsOverlap(m, a, b)));
    return { ...t, cells: { ...t.cells, modifiers: [...kept, gate] } };
}
// Sets how long the sliding door at `index` STARTS: its whole run by default, down to the shortest door the
// game keeps. (A door authored part-way retracted covers only the first `length` cells from its anchor.)
export function withDoorLength(t, index, length) {
    const m = t.cells.modifiers[index];
    if (!m || m.type !== CellModifierType.SlidingDoor || !Array.isArray(m.cells) || m.cells.length < 2) {
        return t;
    }
    const full = runLength(m.cells[0], m.cells[1]);
    const n = Math.max(Math.min(SLIDING_DOOR_MIN_LENGTH, full), Math.min(full, Math.floor(length) || full));
    return m.counter === n ? t : withModifierAt(t, index, { ...m, counter: n });
}
// Swaps the two ends of a run whose order matters: a sliding door's anchor and far end (the housing moves to
// the other end; the length it starts at is kept), or a bolt's head and far end (it is then unlocked from the
// other side).
export function withRunFlipped(t, index) {
    const m = t.cells.modifiers[index];
    const flippable = m && (m.type === CellModifierType.SlidingDoor || m.type === CellModifierType.Bolt);
    if (!flippable || !Array.isArray(m.cells) || m.cells.length < 2) {
        return t;
    }
    return withModifierAt(t, index, { ...m, cells: [m.cells[1], m.cells[0], ...m.cells.slice(2)] });
}
export const withDoorFlipped = withRunFlipped;
export function withLaserPairColor(t, index, color) {
    const m = t.cells.modifiers[index];
    if (!m || m.type !== CellModifierType.LaserGate || (m.pairColor ?? 0) === color) {
        return t;
    }
    return withModifierAt(t, index, { ...m, pairColor: color });
}
// --- any modifier ---------------------------------------------------------------------------------------
export function withModifierRemoved(t, index) {
    if (index < 0 || index >= t.cells.modifiers.length) {
        return t;
    }
    return { ...t, cells: { ...t.cells, modifiers: t.cells.modifiers.filter((_, i) => i !== index) } };
}
function withModifierAt(t, index, mod) {
    return { ...t, cells: { ...t.cells, modifiers: t.cells.modifiers.map((m, i) => (i === index ? mod : m)) } };
}
// A short name for a modifier, for the tree and the inspector headings. Positions are (x,y).
export function modifierLabel(m) {
    const cells = Array.isArray(m.cells) ? m.cells : [];
    const a = cells[0];
    const b = cells[1] ?? a;
    const at = (p) => (p ? `(${p.x},${p.y})` : "(?)");
    switch (m.type) {
        case CellModifierType.Connected:
            return `Link ${at(a)}–${at(b)}`;
        case CellModifierType.LockPanel:
            return `Lock ${at(a)}–${at(b)}`;
        case CellModifierType.MetalBox:
            return `Metal box ${at(a)}–${at(cells[1] ?? (a ? { x: a.x + 1, y: a.y + 1 } : undefined))} · ${m.counter} hit${m.counter === 1 ? "" : "s"}`;
        case CellModifierType.Bolt:
            return `Bolt head ${at(a)}→${at(b)}`;
        case CellModifierType.FrozenBox:
            return `Ice ${at(a)} · ${m.counter} hit${m.counter === 1 ? "" : "s"}`;
        case CellModifierType.SuperMysteryBox:
            return `Super mystery ${at(a)}`;
        case CellModifierType.SlidingDoor:
            return `Door ${at(a)}→${at(b)} · len ${m.counter}`;
        case CellModifierType.LaserGate:
            return `Laser ${at(a)}–${at(b)} · key ${at(cells[2])}`;
        case CellModifierType.Elevator:
            return `Elevator ${at(a)}–${at(b)}`;
        case CellModifierType.GlassPanel:
            return `Glass panel ${at(a)}–${at(b)}`;
        default:
            return `Modifier ${String(m.type)} ${at(a)}`;
    }
}
